import { z } from "zod";
const MAX_CLICKABLE_URL_BYTES = 2_048;
const GOOGLE_WORKSPACE_HOSTS = new Set([
    "calendar.google.com",
    "docs.google.com",
    "drive.google.com",
    "meet.google.com"
]);
export function safeExternalHttpUrl(value) {
    if (value !== value.trim()
        || new TextEncoder().encode(value).byteLength > MAX_CLICKABLE_URL_BYTES
        || /[\u0000-\u001f\u007f]/u.test(value))
        return null;
    try {
        const url = new URL(value);
        return ["http:", "https:"].includes(url.protocol)
            && url.username === ""
            && url.password === ""
            && url.hostname !== ""
            ? value
            : null;
    }
    catch {
        return null;
    }
}
export function safeGoogleWorkspaceUrl(value) {
    if (safeExternalHttpUrl(value) === null)
        return null;
    const url = new URL(value);
    return url.protocol === "https:"
        && url.port === ""
        && GOOGLE_WORKSPACE_HOSTS.has(url.hostname)
        ? value
        : null;
}
export const SafeExternalHttpUrlSchema = z.string().superRefine((value, context) => {
    if (safeExternalHttpUrl(value) === null) {
        context.addIssue({ code: "custom", message: "The clickable URL is unsafe." });
    }
});
export const OpsAssistantReferenceSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("TASK"),
        workItemId: z.uuid(),
        label: z.string().trim().min(1).max(160)
    }).strict(),
    z.object({
        kind: z.literal("WHATSAPP"),
        sourceEventId: z.uuid(),
        label: z.string().trim().min(1).max(160)
    }).strict(),
    z.object({
        kind: z.literal("FILE"),
        sourceEventId: z.uuid(),
        label: z.string().trim().min(1).max(160)
    }).strict(),
    z.object({
        kind: z.literal("GOOGLE_WORKSPACE"),
        linkKind: z.enum(["DRIVE_FILE", "DRIVE_FOLDER", "GOOGLE_DOC", "GOOGLE_SHEET", "GOOGLE_CALENDAR_EVENT", "GOOGLE_MEET"]),
        label: z.string().trim().min(1).max(160),
        url: z.string().superRefine((value, context) => {
            if (safeGoogleWorkspaceUrl(value) === null) {
                context.addIssue({ code: "custom", message: "The Google Workspace URL is unsafe." });
            }
        })
    }).strict().superRefine((reference, context) => {
        if (safeGoogleWorkspaceUrl(reference.url) === null)
            return;
        const url = new URL(reference.url);
        const kindMatches = reference.linkKind === "GOOGLE_DOC"
            ? url.hostname === "docs.google.com" && /^\/document\//u.test(url.pathname)
            : reference.linkKind === "GOOGLE_SHEET"
                ? url.hostname === "docs.google.com" && /^\/spreadsheets\//u.test(url.pathname)
                : reference.linkKind === "DRIVE_FILE"
                    ? url.hostname === "drive.google.com" && (/^\/file\//u.test(url.pathname) || url.pathname === "/open")
                    : reference.linkKind === "DRIVE_FOLDER"
                        ? url.hostname === "drive.google.com" && /^\/drive\//u.test(url.pathname)
                        : reference.linkKind === "GOOGLE_CALENDAR_EVENT"
                            ? url.hostname === "calendar.google.com" && /^\/calendar\//u.test(url.pathname)
                            : url.hostname === "meet.google.com";
        if (!kindMatches) {
            context.addIssue({ code: "custom", path: ["linkKind"], message: "The Google reference kind does not match its URL." });
        }
    })
]);
export const OpsAssistantArtifactSchema = z.object({
    id: z.uuid(),
    kind: z.enum(["DOCUMENT", "SHEET"]),
    displayName: z.string().trim().min(1).max(240),
    mimeType: z.enum(["text/markdown", "application/pdf", "text/csv"]),
    byteCount: z.int().min(1).max(2 * 1024 * 1024),
    sha256: z.string().regex(/^[0-9a-f]{64}$/u),
    downloadUrl: z.string().min(1).max(200),
    /** A revised local copy points at the exact immutable artifact it used. */
    sourceArtifactId: z.uuid().optional(),
    sourceSha256: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
    revision: z.int().positive().optional(),
    preview: z.object({
        kind: z.literal("TEXT"),
        text: z.string().max(8_000),
        truncated: z.boolean()
    }).strict()
}).strict().superRefine((artifact, context) => {
    const expectedUrl = `/api/ops-assistant/artifacts/${artifact.id}/download`;
    if (artifact.downloadUrl !== expectedUrl) {
        context.addIssue({ code: "custom", path: ["downloadUrl"], message: "The artifact download URL does not match its ID." });
    }
    const documentTypeMatches = artifact.mimeType === "text/markdown"
        ? artifact.displayName.toLowerCase().endsWith(".md")
        : artifact.mimeType === "application/pdf"
            ? artifact.displayName.toLowerCase().endsWith(".pdf")
            : artifact.displayName.toLowerCase().endsWith(".csv");
    if ((artifact.kind === "DOCUMENT" && (artifact.mimeType === "text/csv" || !documentTypeMatches))
        || (artifact.kind === "SHEET" && (artifact.mimeType !== "text/csv" || !documentTypeMatches))
        || ((artifact.revision ?? 1) === 1
            && (artifact.sourceArtifactId !== undefined || artifact.sourceSha256 !== undefined))
        || ((artifact.revision ?? 1) > 1
            && (!artifact.sourceArtifactId || !artifact.sourceSha256))) {
        context.addIssue({ code: "custom", message: "The artifact kind, name, and type do not match." });
    }
});
//# sourceMappingURL=ops-assistant-content.js.map